import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsServiceModel } from '@services/query-param/query-param.service.model';
import { asyncScheduler, BehaviorSubject, from, mergeMap, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, throttleTime } from 'rxjs/operators';
import {
  StatefulService
} from '../stateful-service-management/stateful-service-interface/stateful-service-interface';
import QueryParamUpdate = QueryParamsServiceModel.QueryParamUpdate;
import TwoWayBindMappingConfig = QueryParamsServiceModel.TwoWayBindMappingConfig;
import TwoWayBindConfig = QueryParamsServiceModel.TwoWayBindConfig;

/** Contains State */
@Injectable({
  providedIn: 'root'
})
export class QueryParamService implements StatefulService {

  private readonly THROTTLE_TIME = 1000;
  queryParamUpdateBuffer$ = new BehaviorSubject<QueryParamUpdate>(null);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    // this subscription is alive until the app is reset
    this.queryParamUpdateBuffer$.pipe(
      distinctUntilChanged(),
      // only allows one concurrent query param update to occur at a time
      mergeMap(update => this.updateQueryParam(update), 1),
    ).subscribe();
  }

  updateQueryParam(update?: QueryParamUpdate) {
    if (!update) {
      return of(true);
    }
    return from(this.router.navigate(
      [], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        replaceUrl: true,
        queryParams: {
          [update.name]: update.value ?? undefined
        },
      }
    ));
  }

  /**
   * Callers must handle unsubscribing from the returned Subscription!
   */
  twoWayBindFormControl(config: TwoWayBindConfig): Subscription {
    return this.twoWayBindFormControlWithMapping({
      queryParamName: config.queryParamName,
      control: config.control,
      callBackAfterSetControl: config.callBackAfterSetControl,
      // no mapping
      mapQueryToControl: v => v,
      mapControlToQuery: v => v,
    });
  }

  /**
   * Callers must handle unsubscribing from the returned Subscription!
   */
  twoWayBindFormControlWithMapping<T>(config: TwoWayBindMappingConfig<T>): Subscription {
    const subscriptions = new Subscription();
    // Query Params => FormControl
    subscriptions.add(
      this.activatedRoute.queryParams.pipe(
        map(queryParameters => queryParameters[config.queryParamName]),
        distinctUntilChanged(),
        throttleTime(this.THROTTLE_TIME),
        map(queryParamValue => config.mapQueryToControl(queryParamValue)),
      ).subscribe((controlValue) => {
        config.control.setValue(controlValue);
        if (config.callBackAfterSetControl) {
          config.callBackAfterSetControl();
        }
      })
    );
    // FormControl => Query Params
    subscriptions.add(
      config.control.valueChanges.pipe(
        distinctUntilChanged(),
        throttleTime(this.THROTTLE_TIME, asyncScheduler, { leading: true, trailing: true }),
        map(controlValue => config.mapControlToQuery(controlValue)),
      ).subscribe((queryParamValue) => {
        // the update buffer will make sure the query param is updated eventually
        this.queryParamUpdateBuffer$.next({
          name: config.queryParamName,
          value: queryParamValue
        });
      })
    );
    return subscriptions;
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.queryParamUpdateBuffer$.next(null);
  }
}
